import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { CONTAINER_IDS } from '../lib/containers';
import type {
  Task,
  CreateTaskInput,
  UpdateTaskInput,
  MilestoneDomain,
  ApiResponse,
} from '../types/task';
import { useActiveYear } from '../contexts/ActiveYearContext';

const MILESTONES_KEY = 'milestones';
const DOMAINS_KEY = 'milestone-domains';
const TASKS_KEY = 'tasks';
const TEMPLATES_KEY = 'milestone-templates';

/**
 * Hook to list milestones for a given year (defaults to the active year).
 * Pass `year: 'all'` to include every year.
 */
export function useMilestones(options?: { domain?: string; year?: number | 'all' }) {
  const { year: activeYear } = useActiveYear();
  const effectiveYear =
    options?.year !== undefined ? options.year : (activeYear ?? undefined);

  const params = new URLSearchParams();
  if (options?.domain) params.set('domain', options.domain);
  if (effectiveYear !== undefined) params.set('year', String(effectiveYear));
  const qs = params.toString() ? `?${params.toString()}` : '';

  return useQuery({
    queryKey: [MILESTONES_KEY, options?.domain ?? 'all', String(effectiveYear ?? 'pending')],
    queryFn: async () => {
      const res = await api.get<ApiResponse<Task[]>>(`/milestones${qs}`);
      return res.data;
    },
    // Hold the query until we know which year to ask for — otherwise the first
    // fire would hit the server without a `year` param and get whatever the
    // active year happens to be, which then clashes with the resolved query.
    enabled: effectiveYear !== undefined || options?.year === 'all',
  });
}

/**
 * Hook to fetch the list of milestone templates (DB-backed, admin-editable).
 */
export function useMilestoneTemplates() {
  return useQuery({
    queryKey: [TEMPLATES_KEY],
    queryFn: async () => {
      const res = await api.get<ApiResponse<MilestoneTemplate[]>>('/milestones/templates');
      return res.data;
    },
    staleTime: 1000 * 60,
  });
}

/**
 * Hook to fetch a single milestone template by id.
 */
export function useMilestoneTemplate(id: string | undefined) {
  return useQuery({
    queryKey: [TEMPLATES_KEY, id],
    queryFn: async () => {
      const res = await api.get<ApiResponse<MilestoneTemplate>>(
        `/milestones/templates/${encodeURIComponent(id!)}`,
      );
      return res.data;
    },
    enabled: !!id,
  });
}

export interface CreateTemplateInput {
  name: string;
  description?: string;
  items: MilestoneTemplate['items'];
}

export interface UpdateTemplateInput {
  name?: string;
  description?: string;
  items?: MilestoneTemplate['items'];
  version: number;
}

/**
 * Admin-only. Create a new milestone template.
 */
export function useCreateMilestoneTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateTemplateInput) => {
      const res = await api.post<ApiResponse<MilestoneTemplate>>(
        '/milestones/templates',
        input,
      );
      return res.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
    },
  });
}

/**
 * Admin-only. Update an existing milestone template (optimistic locking via version).
 */
export function useUpdateMilestoneTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateTemplateInput & { id: string }) => {
      const res = await api.put<ApiResponse<MilestoneTemplate>>(
        `/milestones/templates/${encodeURIComponent(id)}`,
        input,
      );
      return res.data;
    },
    onSettled: (_data, _err, { id }) => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, id] });
    },
  });
}

/**
 * Admin-only. Delete a milestone template.
 */
export function useDeleteMilestoneTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/milestones/templates/${encodeURIComponent(id)}`);
      return id;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
    },
  });
}

/**
 * Admin-only. Apply a template for a given year.
 */
export function useApplyMilestoneTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { templateId: string; year: number; anchorDate: string }) => {
      const res = await api.post<ApiResponse<Task[]>>(
        `/milestones/templates/${encodeURIComponent(input.templateId)}/apply`,
        { year: input.year, anchorDate: input.anchorDate },
      );
      return res.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [MILESTONES_KEY] });
    },
  });
}

/**
 * Admin-only. Wipe all milestones for a year.
 */
export function useWipeMilestoneYear() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (year: number) => {
      const res = await api.delete<ApiResponse<{ deleted: number; year: number }>>(
        `/milestones?year=${year}`,
      );
      return res.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [MILESTONES_KEY] });
    },
  });
}

export interface MilestoneTemplateItem {
  key: string;
  title: string;
  description?: string;
  domain?: string;
  domains?: string[];
  daysFromAnchor: number;
  /** Year-independent calendar date in "MM-DD" format; overrides daysFromAnchor when set. */
  fixedDate?: string;
  anchorKey?: string;
  anchor?: boolean;
  dependencyKeys?: string[];
}

export interface MilestoneTemplate {
  id: string;
  name: string;
  description: string;
  items: MilestoneTemplateItem[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/**
 * Hook to fetch the configured milestone domains (static per deploy).
 */
export function useMilestoneDomains() {
  return useQuery({
    queryKey: [DOMAINS_KEY],
    queryFn: async () => {
      const res = await api.get<ApiResponse<MilestoneDomain[]>>('/milestones/domains');
      // Exclude fixed nav containers (categories) — only real domain tags are
      // selectable in the milestone template editor. Filtering here means every
      // consumer of this hook (template dropdown, edit modal, etc.) gets the
      // cleaned list centrally.
      return res.data.filter((d) => !CONTAINER_IDS.has(d.id));
    },
    staleTime: 1000 * 60 * 60, // domains rarely change
  });
}

/**
 * Create a milestone. Forces type='milestone' — callers don't need to pass it.
 */
export function useCreateMilestone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: Omit<CreateTaskInput, 'type'>) => {
      const res = await api.post<ApiResponse<Task>>('/tasks', {
        ...input,
        type: 'milestone',
      });
      return res.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [MILESTONES_KEY] });
      queryClient.invalidateQueries({ queryKey: [TASKS_KEY] });
    },
  });
}

/**
 * Update an existing milestone. Uses the generic task PUT.
 */
export function useUpdateMilestone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateTaskInput & { id: string }) => {
      const res = await api.put<ApiResponse<Task>>(`/tasks/${id}`, input);
      return res.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [MILESTONES_KEY] });
      queryClient.invalidateQueries({ queryKey: [TASKS_KEY] });
    },
  });
}

/**
 * Delete a milestone.
 */
export function useDeleteMilestone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/tasks/${id}`);
      return id;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [MILESTONES_KEY] });
      queryClient.invalidateQueries({ queryKey: [TASKS_KEY] });
    },
  });
}
